import { useEffect, useState } from "react";
import BrandMark from "@/components/BrandMark";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, api, formatError } from "@/lib/apiClient";
import { motion } from "framer-motion";
import SiteFooter from "@/components/SiteFooter";

// -------- Shared centered dark card shell --------
function AuthShell({ subtitle, title, children, footer, maxWidth = "max-w-sm" }) {
  return (
    // Column rather than a single centred box, so the footer can sit at the bottom without
    // pulling the sign-in card off centre.
    <div className="min-h-screen bg-[#080C16] text-[#F9F8F6] flex flex-col relative overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-30 blur-3xl"
           style={{ background: "radial-gradient(circle at center, rgba(203,169,108,0.35), transparent 60%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
           style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className={`relative z-10 w-full ${maxWidth}`}
        >
          <Link to="/" className="inline-flex items-center gap-2 text-sm mb-10 opacity-80 hover:opacity-100 transition-opacity">
            <BrandMark />
            <span className="font-serif-display text-lg tracking-tight">DharmaTN</span>
          </Link>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF] mb-3">{subtitle}</div>
          <h1 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight mb-8">{title}</h1>
          <div className="bg-[#111623]/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
            {children}
          </div>
          {footer && <div className="mt-5 text-sm text-[#A0AABF] text-center">{footer}</div>}
        </motion.div>
      </div>
      <SiteFooter className="relative z-10 mt-0" />
    </div>
  );
}

// -------- Admin / Reviewer email+password (centered, no image) --------
export function LoginPage({ role = "customer", subtitle = "Sign in", title = "Welcome back" }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Customer route falls back to social page
  if (role === "customer") return <CustomerAuthPage />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password, role);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "admin") nav("/admin");
      else if (u.role === "reviewer") nav("/advocate");
      else nav("/customer");
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell subtitle={subtitle} title={title}>
      <form onSubmit={submit} className="space-y-4" data-testid={`login-form-${role}`}>
        <div>
          <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Email</Label>
          <Input
            data-testid={`${role}-login-email`}
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C] focus:ring-1 focus:ring-[#CBA96C]"
          />
        </div>
        <div>
          <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Password</Label>
          <Input
            data-testid={`${role}-login-password`}
            type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C] focus:ring-1 focus:ring-[#CBA96C]"
          />
        </div>
        <Button
          data-testid={`${role}-login-submit`}
          disabled={busy}
          className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] hover:shadow-[0_0_20px_rgba(203,169,108,0.25)] font-medium h-11 rounded-lg transition-colors"
        >
          {busy ? "Signing in…" : "Sign In"}
        </Button>
      </form>
    </AuthShell>
  );
}

// -------- Customer: Google + Facebook only (mock in dev) --------
function GoogleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.2-1.5 3.6-5.5 3.6-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 3 14.6 2 12 2 6.9 2 2.8 6.1 2.8 11.6S6.9 21.2 12 21.2c6.9 0 11.5-4.9 10.8-11H12z"/>
    </svg>
  );
}
function FacebookGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path fill="#1877F2" d="M22 12a10 10 0 10-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0022 12z"/>
    </svg>
  );
}

// The reasons the backend's OAuth callback can send back (`/login?oauth_error=<reason>`, see
// `_oauth_fail` in backend/routers/auth.py), in words a customer can act on.
const OAUTH_ERRORS = {
  cancelled: "Sign-in was cancelled. Choose a provider to try again.",
  state: "That sign-in could not be verified. Please try again.",
  expired: "That sign-in took too long and expired. Please try again.",
  unverified_email: "Your email address is not verified with that provider. Verify it there, or use the other provider.",
  staff: "This email belongs to a staff account. Staff sign in from the admin or advocate login page.",
  maintenance: "DharmaTN is down for scheduled maintenance. Please try again shortly.",
  unavailable: "That sign-in option is not available right now.",
  session: "We could not finish signing you in. Please try again.",
  provider: "The sign-in provider did not respond as expected. Please try again in a moment.",
};

/**
 * Where a Google/Facebook sign-in lands after the backend has set the session cookie. Picks up the
 * user and token (`POST /auth/session`), then moves on to where the sign-in started.
 */
export function AuthComplete() {
  const { completeRedirectSignIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    // Same-site paths only — never `//other.site` — mirroring the backend's safe_next.
    const raw = params.get("next") || "/customer";
    const next = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/customer";
    completeRedirectSignIn()
      .then(u => { toast.success(`Welcome, ${u.name || "back"}`); nav(next, { replace: true }); })
      .catch(() => nav("/login?oauth_error=session", { replace: true }));
    // Runs once: a second POST would mint a second token for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="auth-complete" className="min-h-screen bg-[#080C16] text-[#A0AABF] flex items-center justify-center text-sm">
      Signing you in…
    </div>
  );
}

/**
 * The customer sign-in controls on their own, with no page chrome — so the same flow can be shown
 * as the `/login` page *and* inside the landing page's sign-in dialog (`SignInDialog`) without the
 * two drifting apart. `onSignedIn` lets the dialog close itself; navigation to /customer happens
 * either way.
 */
export function CustomerAuthPanel({ onSignedIn }) {
  const { socialLogin } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null); // "google" | "facebook" | null
  const [mockOpen, setMockOpen] = useState(null); // provider being mocked
  const [mockEmail, setMockEmail] = useState("");
  const [mockName, setMockName] = useState("");
  // What each button does is the server's decision (`GET /auth/oauth/providers`): `live` is the real
  // Google/Facebook redirect, `mock` the lab stand-in, `off` hides the button. It used to be read
  // from REACT_APP_* variables baked into the build, so enabling a provider meant a rebuild — and
  // the build and the backend could disagree about whether sign-in was real.
  const [modes, setModes] = useState(null);
  const [params] = useSearchParams();
  const oauthError = params.get("oauth_error");

  useEffect(() => {
    api.get("/auth/oauth/providers")
      .then(r => setModes(r.data))
      .catch(() => setModes({ google: "off", facebook: "off" }));
  }, []);

  // A redirect sign-in that failed comes back here as ?oauth_error=<reason>. Said once as a toast
  // and kept on the card, so it is still there if the toast was missed.
  useEffect(() => {
    if (oauthError) toast.error(OAUTH_ERRORS[oauthError] || OAUTH_ERRORS.provider);
  }, [oauthError]);

  const isMock = modes && Object.values(modes).includes("mock");

  const doSocial = async (provider, email, name) => {
    setBusy(provider);
    try {
      const u = await socialLogin({ provider, email, name });
      toast.success(`Welcome, ${u.name}`);
      onSignedIn?.();
      nav("/customer");
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Social login failed");
    } finally { setBusy(null); setMockOpen(null); setMockEmail(""); setMockName(""); }
  };

  const start = (provider) => {
    if (modes?.[provider] === "live") {
      // A full-page navigation, not an XHR: the provider's sign-in page has to replace ours, and
      // the backend's redirect back sets the session cookie on the way. No Google or Facebook
      // script is ever loaded into this page — see backend/core/oauth.py for why.
      setBusy(provider);
      window.location.assign(`${API}/auth/oauth/${provider}/start?next=/customer`);
    } else if (modes?.[provider] === "mock") {
      // Pre-fill sensible defaults so tap-through demo works
      setMockEmail(provider === "google" ? "mock1@gmail.com" : "mock2@facebook.com");
      setMockName(provider === "google" ? "Mock Google User" : "Mock Facebook User");
      setMockOpen(provider);
    }
  };
  const shows = (provider) => modes && modes[provider] !== "off";

  // Mock step: replaces the provider buttons in place rather than floating over them, so the panel
  // works unchanged inside the landing dialog (a `fixed` child of a transformed card would anchor
  // to the card instead of the viewport).
  if (mockOpen) {
    return (
      <div data-testid="mock-social-dialog">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF]">Mock {mockOpen}</div>
        <div className="font-serif-display text-xl mt-1 mb-5">Sign in with any email</div>
        <div className="space-y-3">
          <div>
            <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Email</Label>
            <Input
              data-testid="mock-social-email" value={mockEmail}
              onChange={e => setMockEmail(e.target.value)}
              className="mt-2 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]"
            />
          </div>
          <div>
            <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Full name</Label>
            <Input
              data-testid="mock-social-name" value={mockName}
              onChange={e => setMockName(e.target.value)}
              className="mt-2 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline" onClick={() => setMockOpen(null)}
              className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5"
            >Back</Button>
            <Button
              data-testid="mock-social-submit"
              onClick={() => doSocial(mockOpen, mockEmail.trim().toLowerCase(), mockName.trim())}
              disabled={!mockEmail || !!busy}
              className="flex-1 bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium"
            >{busy ? "…" : "Continue"}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3" data-testid="customer-social-auth">
        {oauthError && (
          <div data-testid="oauth-error" role="alert"
               className="text-xs leading-relaxed text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/40 rounded-lg px-3 py-2.5">
            {OAUTH_ERRORS[oauthError] || OAUTH_ERRORS.provider}
          </div>
        )}
        {shows("google") && (
          <button
            type="button" onClick={() => start("google")} disabled={!!busy}
            data-testid="social-google-btn"
            className="w-full bg-white text-[#111623] rounded-lg px-4 py-3 hover:bg-white/90 transition-colors flex items-center gap-3 font-medium disabled:opacity-60"
          >
            <GoogleGlyph className="w-5 h-5" />
            <span className="flex-1 text-center text-sm">{busy === "google" ? "Signing in…" : "Continue with Google"}</span>
          </button>
        )}
        {shows("facebook") && (
          <button
            type="button" onClick={() => start("facebook")} disabled={!!busy}
            data-testid="social-facebook-btn"
            className="w-full bg-[#1877F2] text-white rounded-lg px-4 py-3 hover:bg-[#1968D1] transition-colors flex items-center gap-3 font-medium disabled:opacity-60"
          >
            <FacebookGlyph className="w-5 h-5" />
            <span className="flex-1 text-center text-sm">{busy === "facebook" ? "Signing in…" : "Continue with Facebook"}</span>
          </button>
        )}
        {/* Neither provider configured on a production deployment: say so, rather than showing
            buttons that lead nowhere. */}
        {modes && !shows("google") && !shows("facebook") && (
          <div data-testid="social-unavailable" className="text-sm text-[#A0AABF] text-center py-2">
            Customer sign-in is not available right now. Please try again later.
          </div>
        )}
      </div>

      {/* Where the customer agrees to the terms of use, and the only place they can.
          The tick at payment forms the *service* contract for one order — but a customer signs
          in, saves drafts and uploads identity documents (Aadhaar, PAN) long before any money
          moves, and `POST /transactions/{tid}/files` has no payment gate. The clauses that
          govern that period — the warranty that uploaded documents are theirs (2.1/2.2), the
          licence to store and process them (3), prohibited conduct (8), the data consent (9) and
          suspension (12) — have to be agreed at the point of collection, which is here.
          Shown only when there is a provider to continue with: with sign-in unavailable there is
          nothing to agree to. */}
      {(shows("google") || shows("facebook")) && (
        <p data-testid="auth-terms-consent" className="mt-4 text-[11px] leading-relaxed text-[#6B7280] text-center">
          By continuing, you agree to our{" "}
          <Link to="/disclaimer" target="_blank" rel="noopener noreferrer" data-testid="auth-terms-link"
                className="underline text-[#A0AABF] hover:text-white">Disclaimer &amp; Terms of Use</Link>{" "}
          and our{" "}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="auth-privacy-link"
                className="underline text-[#A0AABF] hover:text-white">Privacy Policy</Link>.
        </p>
      )}

      {isMock && (
        <div className="mt-5 text-[11px] leading-relaxed text-[#A0AABF] border-t border-white/5 pt-4">
          Dev mode — any email you enter becomes a customer account. Set
          <span className="text-white/90"> GOOGLE_CLIENT_ID/SECRET</span> or
          <span className="text-white/90"> FACEBOOK_APP_ID/SECRET</span> and
          <span className="text-white/90"> PUBLIC_BASE_URL</span> on the backend for real sign-in.
        </div>
      )}
    </>
  );
}

export function CustomerAuthPage() {
  return (
    <AuthShell subtitle="Customer Portal" title="One tap to your legal workspace" maxWidth="max-w-sm">
      <CustomerAuthPanel />
    </AuthShell>
  );
}

// -------- Signup keeps its route but redirects to social page --------
export function SignupPage() {
  return <CustomerAuthPage />;
}
